/**
 * PageHero - Standardized hero section for all core pages
 *
 * Provides consistent hero sections with title, description, and optional actions.
 * Automatically handles responsive spacing, typography, and layout based on variant.
 *
 * **Loading State Support:**
 * Pass `loading={true}` to render skeleton version automatically, ensuring
 * loading states always match the actual structure.
 *
 * @example Standard hero
 * ```tsx
 * <PageHero
 *   title="About Me"
 *   description="Full-stack developer passionate about building great experiences"
 * />
 * ```
 *
 * @example Loading state
 * ```tsx
 * <PageHero
 *   loading
 *   variant="homepage"
 *   align="center"
 * />
 * ```
 *
 * @example Centered hero
 * ```tsx
 * <PageHero
 *   title="Welcome"
 *   description="Explore our work and ideas"
 *   align="center"
 * />
 * ```
 *
 * @example Homepage hero with actions
 * ```tsx
 * <PageHero
 *   variant="homepage"
 *   title="Hi, I'm Drew"
 *   description="Full-stack developer and designer"
 *   actions={
 *     <div className="flex gap-4">
 *       <Button>View Work</Button>
 *       <Button variant="outline">Contact Me</Button>
 *     </div>
 *   }
 * />
 * ```
 *
 * @example With custom image/avatar
 * ```tsx
 * <PageHero
 *   title="About"
 *   description="Learn more about me"
 *   image={<Avatar src="/avatar.jpg" size="lg" />}
 * />
 * ```
 */

import { ReactNode } from 'react';
import {
  PAGE_LAYOUT,
  HERO_VARIANTS,
  CONTAINER_WIDTHS,
  SPACING,
  CONTAINER_PADDING,
} from '@/lib/design-tokens';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

type HeroVariant = keyof typeof HERO_VARIANTS;
type HeroAlign = 'left' | 'center';

interface PageHeroProps {
  /** Hero title (H1) - can be string or ReactNode for custom content */
  title?: string | ReactNode;
  /** Hero description/tagline */
  description?: string | ReactNode;
  /** Hero variant (determines size and styling) */
  variant?: HeroVariant;
  /** Text alignment */
  align?: HeroAlign;
  /** Optional image/avatar element */
  image?: ReactNode;
  /** Optional action buttons/links */
  actions?: ReactNode;
  /** Section ID attribute (typically "hero") */
  id?: string;
  /** Additional CSS classes for the container */
  className?: string;
  /** Additional CSS classes for the content wrapper */
  contentClassName?: string;
  /** Item count for archive pages (e.g., "5 items") */
  itemCount?: number;
  /** Loading state - renders skeleton version */
  loading?: boolean;
  /** Full-width background (content still constrained) */
  fullWidth?: boolean;
}

export function PageHero({
  title,
  description,
  variant = 'standard',
  align = 'left',
  image,
  actions,
  id = 'hero',
  className,
  contentClassName,
  itemCount,
  loading = false,
  fullWidth = false,
}: PageHeroProps) {
  const styles = HERO_VARIANTS[variant];
  const alignmentClasses = align === 'center' ? 'text-center' : '';
  const imageJustify = align === 'center' ? 'justify-center' : 'justify-center md:justify-start';
  const isCentered = align === 'center';

  // Loading state - render skeleton version
  if (loading) {
    return renderHeroSkeleton({
      variant,
      isCentered,
      imageJustify,
      image,
      actions,
      id,
      className,
      contentClassName,
    });
  }

  const content = (
    <HeroContent
      title={title}
      description={description}
      image={image}
      actions={actions}
      styles={styles}
      imageJustify={imageJustify}
      isCentered={isCentered}
      itemCount={itemCount}
      contentClassName={contentClassName}
      containerClass={
        fullWidth
          ? `mx-auto ${CONTAINER_WIDTHS.prose} ${CONTAINER_PADDING}`
          : cn(PAGE_LAYOUT.hero.container)
      }
    />
  );

  return (
    <section
      id={id}
      className={cn(
        fullWidth ? 'w-full relative overflow-visible' : 'relative overflow-visible',
        PAGE_LAYOUT.hero.padding,
        isCentered && 'flex flex-col items-center',
        className
      )}
    >
      {content}
    </section>
  );
}

/** Props for internal HeroContent component */
interface HeroContentProps {
  title?: string | ReactNode;
  description?: string | ReactNode;
  image?: ReactNode;
  actions?: ReactNode;
  styles: { title: string; description: string };
  imageJustify: string;
  isCentered: boolean;
  itemCount?: number;
  contentClassName?: string;
  containerClass: string;
}

/** Renders the description element (p for strings, div for ReactNode) */
function renderDescription({
  description,
  styles,
  isCentered,
  itemCount,
}: Pick<HeroContentProps, 'description' | 'styles' | 'isCentered' | 'itemCount'>) {
  if (!description) return null;
  const centeredClass = cn('max-w-3xl', isCentered && 'mx-auto');
  if (typeof description === 'string') {
    return (
      <p
        className={cn(styles.description, centeredClass)}
        style={isCentered ? { textAlign: 'center' } : undefined}
      >
        {description}
        {itemCount !== undefined && (
          <span className="text-muted-foreground block md:inline">
            {' '}
            ({itemCount} {itemCount === 1 ? 'item' : 'items'})
          </span>
        )}
      </p>
    );
  }
  return <div className={cn(styles.description, centeredClass)}>{description}</div>;
}

/** Shared hero content layout (image, title, description, actions) */
function HeroContent({
  title,
  description,
  image,
  actions,
  styles,
  imageJustify,
  isCentered,
  itemCount,
  contentClassName,
  containerClass,
}: HeroContentProps) {
  return (
    <div
      className={cn(
        containerClass,
        PAGE_LAYOUT.hero.content,
        'relative z-10',
        isCentered ? 'text-center' : '',
        isCentered && 'flex flex-col items-center w-full',
        contentClassName
      )}
    >
      {image && <div className={cn('flex', imageJustify)}>{image}</div>}
      {title && <h1 className={styles.title}>{title}</h1>}
      {renderDescription({ description, styles, isCentered, itemCount })}
      {actions && <div className={cn('pt-2', isCentered && 'flex justify-center')}>{actions}</div>}
    </div>
  );
}

/** Renders the skeleton loading state for PageHero */
function renderHeroSkeleton({
  variant,
  isCentered,
  imageJustify,
  image,
  actions,
  id,
  className,
  contentClassName,
}: {
  variant: HeroVariant;
  isCentered: boolean;
  imageJustify: string;
  image?: ReactNode;
  actions?: ReactNode;
  id: string;
  className?: string;
  contentClassName?: string;
}) {
  const titleWidth = variant === 'homepage' ? 'w-64' : 'w-48';
  const imageSize =
    variant === 'homepage' ? 'h-32 w-32 md:h-40 md:w-40' : 'h-20 w-20 md:h-24 md:w-24';
  return (
    <section
      id={id}
      className={cn(
        'relative overflow-visible',
        PAGE_LAYOUT.hero.padding,
        isCentered && 'flex flex-col items-center',
        className
      )}
    >
      <div
        className={cn(
          PAGE_LAYOUT.hero.container,
          PAGE_LAYOUT.hero.content,
          isCentered ? 'text-center' : '',
          isCentered && 'flex flex-col items-center w-full',
          contentClassName
        )}
      >
        {(variant === 'homepage' || image !== undefined) && (
          <div className={cn('flex', imageJustify)}>
            <Skeleton className={cn(imageSize, 'rounded-full')} />
          </div>
        )}
        <Skeleton className={cn('h-10 md:h-12', titleWidth, isCentered && 'mx-auto')} />
        <div className={SPACING.compact}>
          <Skeleton className={cn('h-6 w-full max-w-2xl', isCentered && 'mx-auto')} />
          <Skeleton className={cn('h-6 w-3/4 max-w-2xl', isCentered && 'mx-auto')} />
        </div>
        {(variant === 'homepage' || actions !== undefined) && (
          <div className={cn('pt-2 flex gap-3', isCentered && 'justify-center')}>
            <Skeleton className="h-10 w-28" />
            <Skeleton className="h-10 w-32" />
            {variant === 'homepage' && <Skeleton className="h-10 w-36 hidden sm:inline-flex" />}
          </div>
        )}
      </div>
    </section>
  );
}
